import { Component, OnInit } from '@angular/core';
import { IUnusedFacility } from 'src/app/common/models/facility';
import { Routes, Router } from '@angular/router';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  UntypedFormControl,
  UntypedFormBuilder,
  ValidatorFn,
  NgForm,
  ValidationErrors,
  FormGroupDirective,
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { IStoreHandoffCloneDto } from 'src/app/common/models/facilityCloneDto';
import { ToastrService } from 'ngx-toastr';
import { MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import { OrderTimingComponent } from 'src/app/modules/store-settings/pages/order-timing/order-timing.component';
import { FacilitiesService } from 'src/app/services/facilities.service';
import { StoreHandoffService } from 'src/app/services/store-handoff.service';
import { HandoffInfo } from 'src/app/common/models/handoffInfo';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-setup-store',
  templateUrl: './setup-store.component.html',
  styleUrls: ['./setup-store.component.scss'],
  standalone: true,
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    FormsModule,
    ReactiveFormsModule,
    MatInput,
    MatButton,
  ],
})
export class SetupStoreComponent implements OnInit {
  public selectedSourceHandoffInfo: any;
  public sourceHandoffInfo: HandoffInfo[] = [];
  public sourceHandoffInfoLoaded = false;
  public selectedNewFacility: IUnusedFacility | undefined;
  public unusedFacilities: IUnusedFacility[] = [];
  public unusedFacilitiesLoaded = false;
  public setupPageNumber = 1;
  public saveButtonOff = true;

  public setupStoreForm = this.fb.group({
    phone: this.fb.group(
      {
        country: ['US'],
        number: [''],
      },
      { validators: phoneValidator },
    ),
  });
  public phoneErrorMatcher = new PhoneErrorMatcher();

  public onFormSubmit(): void {}

  public routes: Routes = [
    { path: '', redirectTo: '/AppComponent', pathMatch: 'full' },
    { path: 'store-settings/order-timing', component: OrderTimingComponent },
  ];

  constructor(
    public dialogRef: MatDialogRef<SetupStoreComponent>,
    private storeHandoffService: StoreHandoffService,
    private _facilityManager: FacilityManager,
    private facilitiesService: FacilitiesService,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.getSourceFacilities();
    this.getUnusedFacilities();
  }
  // Facilities
  private SourceHandoffInfoRefresh(handoffInfo: HandoffInfo[]): void {
    this.sourceHandoffInfo = handoffInfo;
    this.sourceHandoffInfoLoaded = true;
  }

  private facilitiesUnusedRefresh(facilities: IUnusedFacility[]): void {
    this.unusedFacilities = facilities;
    this.unusedFacilitiesLoaded = true;
  }

  private getSourceFacilities(): void {
    this.storeHandoffService.getAllStoresHandoffInformation().subscribe({
      next: (data) => this.SourceHandoffInfoRefresh(data),
      error: (_) => this.toastr.error('Unable to load Handoff Information'),
    });
  }

  private getUnusedFacilities(): void {
    this.facilitiesService.getUnimportedFacilities().subscribe({
      next: (facilities) => this.facilitiesUnusedRefresh(facilities),
      error: (_) => this.toastr.error('Unable to load Unimported Facilities'),
    });
  }

  // Phone number
  public formatNumber(): void {
    const natNum = this.phoneNumber.number?.national;
    this.phoneNumberControl.setValue(natNum ? natNum : this.phoneNumberDigits);
  }

  private get phoneNumberDigits(): string {
    return this.phoneNumberControl.value.replace(/\D/g, '');
  }

  private get phoneNumberControl(): UntypedFormControl {
    return this.setupStoreForm.get('phone.number') as UntypedFormControl;
  }

  private get phoneGroup(): UntypedFormControl {
    return this.setupStoreForm.get('phone') as UntypedFormControl;
  }

  private get phoneNumber() {
    return parsePhoneNumber(this.phoneNumberDigits, { regionCode: 'US' });
  }

  // Enabled the save button if all values are valid
  public onStateChange(): void {
    this.saveButtonOff =
      this.selectedSourceHandoffInfo === null
      || this.selectedSourceHandoffInfo.facilityId === 0
      || this.selectedNewFacility === null
      || !this.phoneGroup.valid;
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public setupNewFacility(): void {
    const handoffClone: IStoreHandoffCloneDto = {
      sourceFacilityId: parseInt(this.selectedSourceHandoffInfo!.facilityId),
      facilityName: this.selectedNewFacility!.facilityName,
      facilityId: parseInt(this.selectedNewFacility!.facilityId),
      contactPhoneNumber: this.phoneNumberDigits,
      organizationId: 1,
    };

    this.storeHandoffService.cloneStoreHandoffInfo(handoffClone).subscribe({
      next: (_) => {
        this.storeHandoffService.getAllStoresHandoffInformation().subscribe({
          next: (_unused) => {
            this._facilityManager.setCurrentFacility(this.selectedNewFacility!.facilityId);
            this.router.navigate(['/handoff-settings']);
            this.dialogRef.close();
            this.toastr.success('Store ' + this.selectedNewFacility!.facilityId + ' successfully created');
          },
          error: (e) => {
            this.toastr.error('Error while importing Facility');
          },
        });
      },
      error: (e) => {
        this.toastr.error('Error while importing Facility');
      },
    });
  }
}

export const phoneValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const country = 'US';
  const num = control.get('number');
  if (num?.value && country && !parsePhoneNumber(num.value, { regionCode: country }).valid) {
    return { invalidPhone: true };
  } else {
    return null;
  }
};

export class PhoneErrorMatcher implements ErrorStateMatcher {
  public isErrorState(control: UntypedFormControl, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control.value && control.touched && !control?.parent?.valid);
  }
}
